import R from 'app/assets/R'
import { SettingOutlined, EyeOutlined } from '@ant-design/icons'
import { BaseCol } from 'app/components/common/BaseCol'
import { BaseRow } from 'app/components/common/BaseRow'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { BaseButton } from 'app/components/common/BaseButton'
import { BaseFormItem } from 'app/components/common/forms/components/BaseFormItem'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseSpace } from 'app/components/common/BaseSpace'
import { DatePicker } from 'antd'
import BaseText from 'app/components/common/BaseText'
import { colors } from 'parkway-web-common'
import dayjs, { Dayjs } from 'dayjs'
import { generateMonths } from 'app/common/helpers'
import { requestGetSyncLocks, requestUpsertSyncLocks } from 'app/api/accounting'
import {
  ELockModuleType,
  ELockPeriodType,
} from 'app/api/accounting/model/sync-lock'
import SyncLockListItem, { ISyncLock } from '../SyncLockListItem'
import { useVerifyPermissionAndRedirect } from 'app/common/use-verify-permission-redirect'
import {
  FunctionPermissionEnum,
  ModulePermissionEnum,
} from 'app/common/permission-module'

const Setting: React.FC = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [year, setYear] = useState(dayjs().year())
  const [syncLocks, setSyncLocks] = useState<ISyncLock[]>([])
  const { isHavePermissionFunction } = useVerifyPermissionAndRedirect()

  const isSameMonth = (date1: Dayjs, date2: Dayjs): boolean => {
    return date1.diff(date2, 'month') === 0
  }

  const handleViewList = async () => {
    setIsLoading(true)

    const syncLocksResponse = await requestGetSyncLocks({
      startDate: dayjs().year(year).startOf('year').format('YYYY-MM-DD'),
      endDate: dayjs().year(year).endOf('year').format('YYYY-MM-DD'),
      module: ELockModuleType.DAILY_INVOICE,
      periodType: ELockPeriodType.MONTH,
    })

    if (syncLocksResponse?.status) {
      const data = syncLocksResponse?.data || []
      const mappedData = data.map(item => ({
        value: item.periodStart,
        label: dayjs(item.periodStart).format('MM/YYYY'),
        isLocked: item?.isLocked,
      }))

      // Merge generated months with fetched data
      const months = generateMonths(year).map(month => {
        const found = mappedData.find(item =>
          isSameMonth(dayjs(item.value), dayjs(month.value)),
        )
        return found || month
      })

      setSyncLocks(months)
    }

    setIsLoading(false)
  }

  const handleUpsertSyncLocks = async (date: Dayjs, isLocked: boolean) => {
    setSyncLocks(prev => {
      return prev.map(item => ({
        ...item,
        isLoading: isSameMonth(dayjs(item.value), date),
      }))
    })

    const syncLockResponse = await requestUpsertSyncLocks({
      module: ELockModuleType.DAILY_INVOICE,
      periodType: ELockPeriodType.MONTH,
      date: date.startOf('month').toDate(),
      isLocked,
    })

    if (syncLockResponse?.status) {
      setSyncLocks(prev => {
        return prev.map(item => ({
          ...item,
          isLoading: false,
          isLocked: isSameMonth(dayjs(item.value), date)
            ? isLocked
            : item.isLocked,
        }))
      })
    }

    return
  }

  if (
    !isHavePermissionFunction(
      ModulePermissionEnum.ACCOUNTING,
      FunctionPermissionEnum.ACCOUNTING_SETTINGS_SYNC_LOCK,
    )
  ) {
    return <></>
  }

  return (
    <>
      <BaseCol xl={1}>
        <BaseFormItem hiddenLabel>
          <BasePopover
            content={
              <BaseSpace style={{ width: 300 }}>
                <BaseText fontSize={'md'}>
                  {t(R.strings.settings_sync_sivip)}
                </BaseText>
                <BaseRow gutter={6} justify={'space-between'} align={'middle'}>
                  <BaseCol>
                    <DatePicker
                      onChange={value => {
                        setYear(value?.year() || dayjs().year())
                        setSyncLocks([])
                      }}
                      defaultValue={dayjs()}
                      picker="year"
                      disabledDate={d => !d || d.isAfter(dayjs())}
                    />
                  </BaseCol>
                  <BaseCol>
                    <BaseButton
                      type="primary"
                      children={
                        <BaseRow gutter={6} align={'middle'} justify={'start'}>
                          <BaseCol>
                            <EyeOutlined rev={undefined} />
                          </BaseCol>
                          <BaseCol>
                            <BaseText
                              style={{ color: colors.white }}
                              children={t(R.strings.list)}
                            />
                          </BaseCol>
                        </BaseRow>
                      }
                      onClick={handleViewList}
                      loading={isLoading}
                      disabled={isLoading}
                    />
                  </BaseCol>
                </BaseRow>
                <SyncLockListItem
                  syncLocks={syncLocks}
                  handleUpsertSyncLocks={handleUpsertSyncLocks}
                />
              </BaseSpace>
            }
            trigger={'click'}
          >
            <BaseButton children={<SettingOutlined rev={undefined} />} />
          </BasePopover>
        </BaseFormItem>
      </BaseCol>
    </>
  )
}

export default Setting
